import { gql } from '@apollo/client'
import type { DocumentNode } from 'graphql'

import { ImageTypeForUpload } from '../types/image'
import { EntityTypeForUpload } from '../types/upload'

/**
 * Per-entity upload/delete mutations, replacing the generic `uploadGolang` / `deleteImage`.
 *
 * The generic pair took the entity as an ARGUMENT, and IAM authorizes on the GraphQL root
 * field NAME — it cannot read arguments. So no permission mapping for them was expressible,
 * they were left unmapped, and unmapped fails closed: a 403 for every role including admin.
 * That is why hotel logo/cover/image and agency logo uploads returned nothing on stage.
 *
 * Every mutation aliases its result to `result`, so callers read `data.result.message`
 * whatever the entity. Without the alias each caller would need a per-entity branch just to
 * find the response key, which is the same shape of mistake this change exists to remove.
 */

const uploadDoc = (field: string) => gql`
  mutation ${field[0].toUpperCase()}${field.slice(1)}(
    $entityID: String!
    $entityCode: String!
    $file: Upload!
  ) {
    result: ${field}(entityID: $entityID, entityCode: $entityCode, file: $file) {
      message
    }
  }
`

const deleteDoc = (field: string) => gql`
  mutation ${field[0].toUpperCase()}${field.slice(1)}(
    $mediaID: String!
    $entityCode: String!
  ) {
    result: ${field}(mediaID: $mediaID, entityCode: $entityCode) {
      message
    }
  }
`

export const DELETE_ROOM_TYPE_IMAGE_MUTATION = gql`
  mutation DeleteRoomTypeImage(
    $mediaID: String!
    $entityCode: String!
    $roomTypeCode: String!
  ) {
    result: deleteRoomTypeImage(
      mediaID: $mediaID
      entityCode: $entityCode
      roomTypeCode: $roomTypeCode
    ) {
      message
    }
  }
`

export const UPLOAD_PAYMENT_RECEIPT_MUTATION = uploadDoc(
  'uploadPaymentReceiptAttachment'
)
export const DELETE_PAYMENT_RECEIPT_MUTATION = deleteDoc(
  'deletePaymentReceiptAttachment'
)

/**
 * The eight (entity, image) pairs the panel actually sends. Deliberately NOT every
 * combination the enums allow — a pair with no screen behind it has no mutation on the BFF
 * either, so listing it here would produce a document that fails schema validation at
 * request time rather than at build time.
 *
 * This is the single list. `MUTATIONS` is a total `Record` over it, so a key here without an
 * entry below (or an entry below without a key here) is a compile error, and
 * `SupportedUploadPair` is derived from it rather than restated.
 */
type SupportedPairKey =
  | 'Accommodation:Logo'
  | 'Accommodation:Cover'
  | 'Accommodation:Image'
  | 'RoomType:Image'
  | 'Channel:Logo'
  | 'Provider:Logo'
  | 'Tenant:Logo'
  | 'PaymentReceipt:Attachment'

/**
 * The (entityType, imageType) prop pairs a component may be given, as a correlated union —
 * the two are NOT independent props.
 *
 * `EntityTypeForUpload` is wider than this on purpose: it names every entity the asset
 * service knows about, including `Booking` and `WBSSite`, which have no upload mutation. Two
 * loose props typed with the full unions admit 32 combinations against the 8 that exist, so
 * `<UploadBox entityType="WBSSite" imageType="Logo" />` used to compile and then throw from
 * `mutationsFor` — during render, before any hook, with no error boundary anywhere in this
 * app to catch it. That is a blank page, which is a worse diagnostic than the 403 this whole
 * module exists to remove.
 *
 * Spending the type here makes an unsupported pair a build error instead. The throw below
 * stays as a runtime backstop for callers the compiler cannot see (untyped JS, tests).
 */
export type SupportedUploadPair = {
  [K in SupportedPairKey]: K extends `${infer E}:${infer I}`
    ? {
        entityType: Extract<EntityTypeForUpload, E>
        imageType: Extract<ImageTypeForUpload, I>
      }
    : never
}[SupportedPairKey]

const MUTATIONS: Record<
  SupportedPairKey,
  { upload: DocumentNode; remove: DocumentNode }
> = {
  'Accommodation:Logo': {
    upload: uploadDoc('uploadAccommodationLogo'),
    remove: deleteDoc('deleteAccommodationLogo')
  },
  'Accommodation:Cover': {
    upload: uploadDoc('uploadAccommodationCover'),
    remove: deleteDoc('deleteAccommodationCover')
  },
  'Accommodation:Image': {
    upload: uploadDoc('uploadAccommodationImage'),
    remove: deleteDoc('deleteAccommodationImage')
  },
  'RoomType:Image': {
    upload: uploadDoc('uploadRoomTypeImage'),
    remove: DELETE_ROOM_TYPE_IMAGE_MUTATION
  },
  'Channel:Logo': {
    upload: uploadDoc('uploadChannelLogo'),
    remove: deleteDoc('deleteChannelLogo')
  },
  'Provider:Logo': {
    upload: uploadDoc('uploadProviderLogo'),
    remove: deleteDoc('deleteProviderLogo')
  },
  'Tenant:Logo': {
    upload: uploadDoc('uploadTenantLogo'),
    remove: deleteDoc('deleteTenantLogo')
  },
  'PaymentReceipt:Attachment': {
    upload: UPLOAD_PAYMENT_RECEIPT_MUTATION,
    remove: DELETE_PAYMENT_RECEIPT_MUTATION
  }
}

/**
 * Resolves the mutation pair for an (entity, image) combination.
 *
 * Takes the WIDE types deliberately, even though every typed caller now passes a
 * `SupportedUploadPair`. This is the runtime backstop, and a backstop that only accepts
 * already-valid input is not one — narrowing the parameters here would make the guard below
 * unreachable and delete the protection for callers the compiler never sees.
 *
 * Throws rather than falling back to the deprecated generic mutation. A fallback would be
 * silently wrong in the one way that matters: the generic field is unmapped in IAM, so the
 * user would get an opaque 403 and the cause would be invisible. A thrown error names the
 * missing pair.
 */
export const mutationsFor = (
  entityType: EntityTypeForUpload,
  imageType: ImageTypeForUpload
): { upload: DocumentNode; remove: DocumentNode } => {
  const pair = MUTATIONS[`${entityType}:${imageType}` as SupportedPairKey] as
    | { upload: DocumentNode; remove: DocumentNode }
    | undefined

  if (!pair) {
    throw new Error(
      `No per-entity upload mutation for ${entityType}/${imageType}. ` +
        `Add it to MUTATIONS here and to the BFF schema — the generic upload is ` +
        `deprecated and unmapped in IAM, so there is no working fallback.`
    )
  }

  return pair
}
